import { useDisclosure } from "@chakra-ui/hooks";
import { useQuery } from "@tanstack/react-query";
import CustomButton from "components/button";
import Card from "components/card";
import InputField from "components/fields/InputField";
import Loading from "components/loading";
import CustomModal from "components/modals";
import CustomPagination from "components/pagination";
import SimpleTable from "components/tables/SimpleTable";
import { useState } from "react";
import { MdRemoveRedEye } from "react-icons/md";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { reqFunction } from "utils/API";
import { renderTimeJalali } from "utils/TimeUtiles";
import { CountOption, countSelect } from "variables";
// import { renderToast } from "utils/globalUtils";

export type MqttLogObject = {
  log_id: string;
  topic_name: string;
  message: string;
  state: "Receive" | "Error";
  create_date: string;
  all_mqtt_logs: number;
};
type MqttLogTable = Array<{
  deviceSerial: string;
  topicName: string;
  messageCreateDate: JSX.Element;
  messageStatus: JSX.Element;
  messageAction: JSX.Element;
}>;
type MessageObject = {
  DevInfo?: {
    SerialNum: string;
    DataCounter: number;
    SignalQuality: number;
    DeviceOnTime: number;
    RelayStatus: boolean;
    FirmwareVersion: string;
    DateTime: string;
  };
  Volume?: {
    Cumulative: number;
    Type: string;
    Value: number;
  };
  Flow?: {};
  Voltage?: {
    Battery: number;
    Backup: number;
    RTC: number;
  };
  Temperature?: {};
};
const MqttLogs = () => {
  const [page, setPage] = useState<number>(1);
  const [count, setCount] = useState<CountOption>(countSelect[0]);
  const [message, setMessage] = useState<string | null>(null);
  const [searchInput, setSearchinput] = useState<string>("");
  const [topicFilter, setTopicFilter] = useState<string | null>("meters/data");
  const [logCount, setLogCount] = useState<number>(100);

  const {
    isOpen: isLiveLogOpen,
    onOpen: onLiveLogOpen,
    onClose: onLiveLogClose,
  } = useDisclosure();

  const { data: PureLogData, isLoading: PureLogIsLoading } = useQuery({
    queryKey: ["PureLog", isLiveLogOpen, logCount],
    queryFn: () =>
      reqFunction("Log/admin/mqttLog/pureLog", {
        count: logCount,
        topic: "/meters/data",
      }),
    enabled: isLiveLogOpen,
    refetchInterval: 2000, // 2 seconds
    refetchIntervalInBackground: true,
  });

  const {
    data: MqttLogData,
    isLoading: MqttLogIsLoading,
    status: MqttLogStatus,
    // refetch: MqttLogsRefetch,
  } = useQuery({
    queryFn: () =>
      reqFunction("Log/admin/mqttLog/getAll", {
        page: page,
        count: count.value,
        message__icontains: searchInput ? searchInput : null,
        topic_name: topicFilter ? topicFilter : null,
      }),
    queryKey: ["MqttLogsList", page, count, searchInput, topicFilter],
  });

  const {
    isOpen: isLogOpen,
    onOpen: onLogOpen,
    onClose: onLogClose,
  } = useDisclosure();

  const TableHeader = [
    { title: "سریال کنتور", headerKey: "deviceSerial" },
    { title: "نام تاپیک", headerKey: "topicName" },
    { title: "زمان دریافت", headerKey: "messageCreateDate" },
    { title: "وضعیت پیام", headerKey: "messageStatus" },
    { title: "عملیات", headerKey: "messageAction" },
  ];
  const tableData = () => {
    let logTableData: MqttLogTable = [];
    if (!MqttLogIsLoading && MqttLogStatus === "success") {
      MqttLogData?.data?.forEach((obj: MqttLogObject) => {
        logTableData.push({
          deviceSerial: showSerial(obj.message),
          // deviceSerial: "",
          topicName: obj.topic_name,
          messageCreateDate: renderTimeJalali(obj.create_date, "chart"),
          messageStatus: (
            <span
              className={`rounded ${
                obj.state === "Receive" ? "bg-green-600" : "bg-red-600"
              } px-1 text-sm text-white`}
            >
              {obj.state === "Receive"
                ? "پیام دریافتی دستگاه"
                : "پیام دریافت شده با خطا"}
            </span>
          ),
          messageAction: renderLogAction(obj.message, obj.log_id),
        });
      });
    }
    return logTableData;
  };

  // const deleteLogClick = async (logId: string) => {
  //   // const response = await reqFunction("Log/admin/mqttLog/getAll", {
  //   //   other_admin_id: logId,
  //   // });
  //   // if (response.code === 200) {
  //   //   renderToast("لاگ Mqtt با موفقیت حذف شد.", "success");
  //   //   MqttLogsRefetch();
  //   // } else {
  //   //   renderToast(
  //   //     response?.farsi_message
  //   //       ? response.farsi_message
  //   //       : "در حذف لاگ Mqtt مشکلی رخ داده",
  //   //     "err"
  //   //   );
  //   //console.log(logId);
  // };

  const renderLogAction = (message: string, logId: string) => {
    return (
      <div className=" flex items-center justify-center">
        <CustomButton
          onClick={() => renderMessage(message)}
          icon={<MdRemoveRedEye />}
          color="blue"
          extra="!p-2"
        />
        {/* <CustomButton
          onClick={() => deleteLogClick(logId)}
          icon={<MdDelete />}
          color="red"
          extra="!p-2"
        /> */}
      </div>
    );
  };
  const showSerial = (message: string) => {
    try {
      // Try JSON first
      let correctMessage = message
        .replace(/\s/g, "")
        .replace(/\?/g, "")
        .replace(/\bnan\b/g, "null");
      let m: MessageObject = JSON.parse(correctMessage);
      const serial = m?.DevInfo?.SerialNum ?? "";

      // hide serials longer than 15 characters
      if (serial && serial.length <= 15) {
        return serial;
      }
      return ""; // don't show anything
    } catch {
      // Semicolon format
      if (message.includes(";")) {
        const parts = message.split(";");
        const serial = parts[0] ?? "";
        if (serial.length <= 15) {
          return serial;
        }
      }
      return "";
    }
  };

  const renderMessage = (message: string) => {
    try {
      let correctMessage = message
        .replace(/\s/g, "")
        .replace(/\?/g, "")
        .replace(/\bnan\b/g, "null");
      let parsed = JSON.parse(correctMessage);
      setMessage(parsed);
    } catch {
      // Show raw string if not JSON
      setMessage(message);
    }
    onLogOpen();
  };

  return (
    <div className=" users-table-container">
      <div className="relative flex flex-col gap-3 px-4 pb-2 pt-0 sm:flex-row sm:items-center sm:justify-between">
        {/* Title */}
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          پیام ها
        </div>

        {/* Filters */}
        <div className="moldal-btns flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          <div className="w-full sm:w-auto">
            <CustomButton
              onClick={onLiveLogOpen}
              color="blue"
              extra="whitespace-nowrap px-4 py-2"
              text="مشاهده لاگ زنده"
            />
          </div>
          <div className="w-full sm:w-[200px]">
            <InputField
              id="project-name"
              placeholder="جستجو در لاگ"
              type="text"
              state={searchInput}
              setState={setSearchinput}
            />
          </div>
          <div className="w-full sm:w-[200px]">
            <select
              value={topicFilter ?? ""}
              onChange={(e) => setTopicFilter(e.target.value || null)}
              className="mt-2 h-12 w-full rounded-xl border border-gray-200 bg-white/0 px-3 py-[10px] text-sm text-gray-700 outline-none dark:border-white/10 dark:bg-navy-700 dark:text-white"
            >
              <option value="">همه تاپیک‌ها</option>
              <option value="meters/data">meters/data</option>
              <option value="meters/events">meters/events</option>
            </select>
          </div>
        </div>
      </div>
      {!MqttLogIsLoading && MqttLogStatus === "success" ? (
        <>
          {" "}
          <CustomModal
            isOpen={isLogOpen}
            onClose={onLogClose}
            title={"پیام"}
            modalType="confirmation"
            information={
              <SyntaxHighlighter
                language="json"
                style={vscDarkPlus}
                customStyle={{ whiteSpace: "pre-wrap !important" }}
              >
                {typeof message === "string"
                  ? message
                  : JSON.stringify(message, null, 2)}
                {/* {message} */}
              </SyntaxHighlighter>
            }
          />
          <CustomModal
            isOpen={isLiveLogOpen}
            onClose={onLiveLogClose}
            title={`لاگ زنده MQTT (topic=/meters/data)`}
            modalType="confirmation"
            information={
              PureLogIsLoading ? (
                <Loading />
              ) : (
                <div className="flex flex-col gap-2">
                  {/* count selector row */}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-300">
                      نمایش آخرین
                    </span>
                    <select
                      value={logCount}
                      onChange={(e) => setLogCount(Number(e.target.value))}
                      className="h-8 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-800 outline-none dark:border-white/20 dark:bg-navy-700 dark:text-white"
                    >
                      <option value={100}>100 خط</option>
                      <option value={200}>200 خط</option>
                      <option value={500}>500 خط</option>
                      <option value={1000}>1000 خط</option>
                    </select>
                  </div>

                  {/* log content */}
                  <div className="max-h-[70vh] overflow-y-auto font-mono text-xs">
                    {Array.isArray(PureLogData?.data) &&
                    PureLogData.data.length > 0 ? (
                      <div className="space-y-1">
                        {PureLogData.data.map((line: string, idx: number) => (
                          <div
                            key={idx}
                            className={`flex items-start gap-3 rounded px-2 py-1.5 
                    ${
                      idx % 2 === 0
                        ? "bg-gray-50 dark:bg-navy-800/60"
                        : "bg-white dark:bg-navy-700"
                    }`}
                          >
                            {/* line number */}
                            <span className="w-8 text-right text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                              {idx + 1}
                            </span>

                            {/* text */}
                            <span className="flex-1 whitespace-pre-wrap break-all text-[11px] text-gray-800 dark:text-gray-100">
                              {line}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-4 text-center text-gray-500 dark:text-gray-400">
                        هیچ لاگی برای topic=/meters/data پیدا نشد.
                      </div>
                    )}
                  </div>
                </div>
              )
            }
          />
          <Card extra="!h-[60vh] !mt-0">
            <div className="table-container mx-auto  w-full">
              <SimpleTable
                header={TableHeader}
                data={tableData()}
                hasIndex={true}
                extra="h-[55vh]"
                page={page}
                count={count.value}
              />
            </div>
          </Card>
          <div className="pagination ">
            <CustomPagination
              page={page}
              pageSize={count}
              totalData={
                Array.isArray(MqttLogData?.data) && MqttLogData.data.length > 0
                  ? MqttLogData.data[0].all_mqtt_logs
                  : 0
              }
              changePageFunc={setPage}
              changeCount={setCount}
            />
          </div>
        </>
      ) : (
        // <></>
        <Loading />
      )}
    </div>
  );
};
export default MqttLogs;
